import { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Sort as SortIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';

export interface SortOption {
  id: string;
  label: string;
  field: string;
}

export interface SortConfig {
  field: string;
  direction: 'asc' | 'desc';
}

interface SortMenuProps {
  options: SortOption[];
  value: SortConfig;
  onChange: (sort: SortConfig) => void;
}

/**
 * Reusable sort menu component with ascending/descending options
 */
export function SortMenu({ options, value, onChange }: SortMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (field: string) => {
    // If same field, toggle direction; otherwise, default to ascending
    const direction =
      value.field === field && value.direction === 'asc' ? 'desc' : 'asc';
    onChange({ field, direction });
    handleClose();
  };

  const currentOption = options.find((opt) => opt.field === value.field);
  const sortLabel = currentOption
    ? `${currentOption.label} (${value.direction === 'asc' ? 'A-Z' : 'Z-A'})`
    : 'Sort';

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<SortIcon />}
        onClick={handleClick}
        aria-controls={open ? 'sort-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        {sortLabel}
      </Button>
      <Menu
        id="sort-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'sort-button',
        }}
      >
        {options.map((option) => {
          const isSelected = value.field === option.field;
          const isAsc = isSelected && value.direction === 'asc';

          return (
            <MenuItem
              key={option.id}
              onClick={() => handleSelect(option.field)}
              selected={isSelected}
            >
              <ListItemIcon>
                {isSelected ? (
                  isAsc ? (
                    <ArrowUpwardIcon fontSize="small" />
                  ) : (
                    <ArrowDownwardIcon fontSize="small" />
                  )
                ) : null}
              </ListItemIcon>
              <ListItemText>{option.label}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
